import { DashboardContentStyles, DashboardGrid, GeneralInfoWrapper } from "./Dashboard.styles";
import AveragePointsChart from "./widgets/AveragePointsChart";
import TotalPointsChart from "./widgets/TotalPointsChart";
import ActionModulesDoneChart from "./widgets/ActionModulesDoneChart";
import PresenceQuotaChart from "./widgets/PresenceQuotaChart";
import GeneralInfoData from "./widgets/GeneralInfo/GeneralInfoData";
import { EvaluationStarsChart } from "./widgets/EvaluationStarsChart";
import { useActionsData } from "../../store/selectors/actionModulesSelectors";
import { CoinsIcon, FamilyStarIcon } from "../../assets/icons/Icons";
import { useTranslation } from "react-i18next";
import { PersonIcon, ThumbUpIcon } from "../../assets/icons/ActionIcons";
import { theme } from "../../theme";
import { EvaluationThumbsChart } from "./widgets/EvaluationThumbsChart";
import { useCallback, useEffect, useMemo } from "react";
import { Subtype } from "../../types/actionModule/subtypes/subtype";
import UpcomingBirthdaysChart from "./widgets/UpcomingBirthdaysChart";
import UpcomingAnniversariesChart from "./widgets/UpcomingAnniversariesChart";
import { WidgetContainer, WidgetTitle } from "./widgets/Widget.styles";
import ChecklistAnswerStreakChart from "./widgets/ChecklistAnswerStreakChart";
import LeaderboardWidget from "./widgets/LeaderboardWidget";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { fetchServiceModulesThunk } from "../../store/thunk/serviceModulesThunk";
import { useServiceModules } from "../../store/selectors/serviceModulesSelector";
import DropdownAnswerStreakChart from "./widgets/DropdownAnswerStreakChart";

interface WidgetConfig {
  title: string;
  icon: React.ElementType;
  component: () => JSX.Element;
  condition: boolean;
}

const DashboardContent = () => {
  const { t } = useTranslation();
  const actionsData = useActionsData();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchServiceModulesThunk());
  }, [dispatch]);

  const allSubtypes = useMemo(
    () => [...new Set(actionsData.flatMap((a) => a.subtypes.map((s) => s.subtype)))],
    [actionsData]
  );

  const serviceModules = useServiceModules();
  const hasLeaderboardModules = serviceModules.some((mod) => mod.type === "Leaderboard");

  const renderWidget = useCallback((widget: WidgetConfig) => (
    <WidgetContainer key={widget.title}>
      <WidgetTitle variant="h6">{widget.title}</WidgetTitle>
      {widget.component()}
    </WidgetContainer>
  ), []);

  const widgetConfig = [
    {
      title: t("dashboardPage.averagePoints.title"),
      icon: CoinsIcon,
      component: () => <AveragePointsChart />,
      condition: true,
    },
    {
      title: t("dashboardPage.totalPoints.title"),
      icon: CoinsIcon,
      component: () => <TotalPointsChart />,
      condition: true,
    },
    {
      title: t("dashboardPage.actionModulesDone.title"),
      icon: CoinsIcon,
      component: () => <ActionModulesDoneChart />,
      condition: true,
    },
    {
      title: t("dashboardPage.presenceQuota.title"),
      icon: CoinsIcon,
      component: () => <PresenceQuotaChart />,
      condition: allSubtypes.includes(Subtype.PresenceQuotaModuleSubtype),
    },
    {
      title: t("dashboardPage.evaluationStars.title"),
      icon: FamilyStarIcon,
      component: () => <EvaluationStarsChart />,
      condition: allSubtypes.includes(Subtype.EvaluationStarsModuleSubtype)
    },
    ...actionsData
      .filter((action) =>
        action.subtypes.map((s) => s.subtype).includes(Subtype.EvaluationThumbsModuleSubtype) &&
        action.subtypes.map((s) => s.subtype).includes(Subtype.TextAnswerModuleSubtype)
      )
      .map((action, index) => ({
        title: `${t("dashboardPage.evaluationThumbs.title")} (${action.name})`,
        icon: ThumbUpIcon,
        component: () => <EvaluationThumbsChart actionModuleId={action.id} />,
        condition: true,
      })),
    {
      title: t("dashboardPage.upcomingBirthdays.title"),
      icon: (props) => (
        <PersonIcon {...props} colorHex={theme.palette.companyMain.main} />
      ),
      component: () => <UpcomingBirthdaysChart />,
      condition: allSubtypes.includes(Subtype.BirthdayBonusModuleSubtype),
    },
    {
      title: t("dashboardPage.upcomingAnniversaries.title"),
      icon: (props) => (
        <PersonIcon {...props} colorHex={theme.palette.companyMain.main} />
      ),
      component: () => <UpcomingAnniversariesChart />,
      condition: allSubtypes.includes(Subtype.AnniversaryBonusModuleSubtype),
    },
    {
      title: t("dashboardPage.checklistAnswerStreak.title"),
      icon: (props) => (
        <PersonIcon {...props} colorHex={theme.palette.companyMain.main} />
      ),
      component: () => <ChecklistAnswerStreakChart />,
      condition: allSubtypes.includes(Subtype.ChecklistAnswerModuleSubtype),
    },
    {
      title: t("dashboardPage.dropdownAnswerStreak.title"),
      icon: (props) => (
        <PersonIcon {...props} colorHex={theme.palette.companyMain.main} />
      ),
      component: () => <DropdownAnswerStreakChart />,
      condition: allSubtypes.includes(Subtype.DropdownAnswerModuleSubtype),
    },
    {
      title: t("dashboardPage.leaderboardWidget.title"),
      icon: (props) => (
        <PersonIcon {...props} colorHex={theme.palette.companyMain.main} />
      ),
      component: () => <LeaderboardWidget />,
      condition: hasLeaderboardModules,
    },
  ];

  return (
    <DashboardContentStyles>
      <GeneralInfoWrapper >
        <GeneralInfoData />
      </GeneralInfoWrapper >
      <DashboardGrid>
        {widgetConfig.filter(widget => widget.condition).map(renderWidget)}
      </DashboardGrid>
    </DashboardContentStyles>
  );
};

export default DashboardContent;
